// usePasswordRecovery (contract 06 §9) — wraps AuthPasswordRecoveryFlow. Known and unknown
// emails resolve to the same Login banner (no enumeration). The reset token stays composable-
// LOCAL (returned from the exchange, never persisted or routed). Password mismatch is caught here.
import { ref, type Ref } from 'vue'
import { useRouter } from 'vue-router'

import { AuthPasswordRecoveryFlow } from '../flows/AuthPasswordRecoveryFlow'
import { AuthError } from '../models/auth.error'
import type { ResetCredential } from '../models/auth.types'

export interface UsePasswordRecovery {
  pending: Ref<boolean>
  error: Ref<string | null>
  requestReset: (email: string) => Promise<void>
  exchangeResetHandoff: (fragment: string) => Promise<string | null>
  changePassword: (
    credential: ResetCredential,
    password: string,
    confirmation: string,
  ) => Promise<void>
}

export function usePasswordRecovery(): UsePasswordRecovery {
  const router = useRouter()
  const flow = new AuthPasswordRecoveryFlow()

  const pending = ref(false)
  const error = ref<string | null>(null)

  async function requestReset(email: string): Promise<void> {
    pending.value = true
    error.value = null
    try {
      await flow.requestResetLink(email)
      await router.push({ name: 'Login', query: { show_reset_info: '1' } })
    } catch (e) {
      error.value = AuthError.from(e).i18nKey
    } finally {
      pending.value = false
    }
  }

  /** Exchange the one-time #reset_handoff fragment; the returned token stays with the caller. */
  async function exchangeResetHandoff(fragment: string): Promise<string | null> {
    error.value = null
    try {
      return await flow.exchangeResetHandoff(fragment)
    } catch {
      error.value = 'auth.reset.invalidCode'
      return null
    }
  }

  async function changePassword(
    credential: ResetCredential,
    password: string,
    confirmation: string,
  ): Promise<void> {
    if (password !== confirmation) {
      error.value = 'auth.reset.mismatch'
      return
    }
    pending.value = true
    error.value = null
    try {
      await flow.changePassword(credential, password, confirmation)
      await router.push({ name: 'Login', query: { change_success: '1' } })
    } catch (e) {
      error.value = AuthError.from(e).i18nKey
    } finally {
      pending.value = false
    }
  }

  return { pending, error, requestReset, exchangeResetHandoff, changePassword }
}
